/**
 * 語音管家 API Service（#24 Phase C2）。
 * NLU（8 intent）+ 3 voice-query（reuse my_calendar/my_contacts/notifications）。
 */
import axiosInstance from '../api/axios';

const BASE = '/api/ee/voice';

export interface NluResult {
  intent: string | null;
  entities: Record<string, any>;
  confidence: number;
  raw: string;
}

export interface VoiceQueryResult {
  intent: string;
  text_for_tts: string;
  structured: Record<string, any>;
}

/** 語音文字 → 結構化意圖（rule-based）。 */
export const dispatchNlu = async (utterance: string): Promise<NluResult> => {
  const res = await axiosInstance.post<NluResult>(`${BASE}/nlu`, { utterance });
  return res.data;
};

export const calendarQuery = async (dateFrom: string, dateTo: string, keyword?: string): Promise<VoiceQueryResult> => {
  const res = await axiosInstance.post<VoiceQueryResult>(`${BASE}/calendar-query`, {
    date_from: dateFrom, date_to: dateTo, keyword: keyword || null,
  });
  return res.data;
};

export const contactLookup = async (contactName: string): Promise<VoiceQueryResult> => {
  const res = await axiosInstance.post<VoiceQueryResult>(`${BASE}/contact-lookup`, { contact_name: contactName });
  return res.data;
};

export const notificationRead = async (latestOnly = false, count?: number): Promise<VoiceQueryResult> => {
  const res = await axiosInstance.post<VoiceQueryResult>(`${BASE}/notification-read`, {
    latest_only: latestOnly, count: count || null,
  });
  return res.data;
};

// ── 麥克風權限（porsche 2026-08-19：VoicePage permission-aware + denied 重置指引）──
export type MicPermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

/** 查詢麥克風權限狀態（不觸發授權彈窗）。Safari 舊版無 Permissions API → 'unsupported'。 */
export async function checkMicPermission(): Promise<MicPermission> {
  if (!navigator.permissions || !navigator.permissions.query) return 'unsupported';
  try {
    const perm = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return perm.state as MicPermission;
  } catch {
    return 'unsupported';   // 某些瀏覽器不認 'microphone' 這個 permission name
  }
}

/** 主動請求麥克風（會觸發瀏覽器授權彈窗）；成功即釋放 track。回 true=已授權。 */
export async function requestMicPermission(): Promise<boolean> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return false;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((tr) => tr.stop());   // 只為拿授權，拿完即釋放
    return true;
  } catch {
    return false;
  }
}

export type MicGuideKey = 'chrome' | 'firefox' | 'safari' | 'ios' | 'android' | 'generic';

/** 由 userAgent 判斷該顯示哪一份重置指引。 */
export function detectMicGuide(ua: string = navigator.userAgent): MicGuideKey {
  const s = ua.toLowerCase();
  if (/iphone|ipad|ipod/.test(s)) return 'ios';                 // iOS 一律走 iOS Safari 指引
  if (/android/.test(s)) return 'android';                      // Android 多為 Chrome 核心
  if (/firefox|fxios/.test(s)) return 'firefox';
  if (/edg\//.test(s) || /chrome|crios/.test(s)) return 'chrome';   // Edge 也走 Chrome 指引
  if (/safari/.test(s)) return 'safari';                        // 桌機 Safari（排除上面 Chrome 後）
  return 'generic';
}

/**
 * 依 NLU 結果 dispatch 到對應 query endpoint。
 * 回 VoiceQueryResult（含 text_for_tts）；不支援的 intent 回 null。
 */
export const runIntent = async (nlu: NluResult): Promise<VoiceQueryResult | null> => {
  const e = nlu.entities || {};
  switch (nlu.intent) {
    case 'calendar_query':
      return calendarQuery(e.date_from, e.date_to, e.keyword);
    case 'contact_lookup':
      return e.contact_name ? contactLookup(e.contact_name) : null;
    case 'notification_read':
      return notificationRead(!!e.latest_only, e.count);
    default:
      return null;   // 分享類 intent（#198）由 drive-share UI 處理，語音頁不 dispatch
  }
};
